import React, { FC, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import SaveButton from "../../components/SaveButton";
import { DialogController } from "../../hooks/use-dialog";
import { useRenewApiUserRefreshTokenMutation } from "../../store/pomeriumApi";
import CodeSnippet from "@/components/CodeSnippet";

export type RefreshTokenParams = {
  orgId: string;
  userId: string;
};

type Props = {
  dialog: DialogController<RefreshTokenParams>;
};

const RefreshToken: FC<Props> = ({ dialog }) => {
  const [token, setToken] = useState("");
  const [refreshToken, tokenResult] = useRenewApiUserRefreshTokenMutation();

  const clickRefresh = async () => {
    if (dialog?.data?.orgId && dialog?.data?.userId) {
      await refreshToken({
        organizationId: dialog.data.orgId,
        userId: dialog.data.userId,
      });
    }
  };

  useEffect(() => {
    if (tokenResult.isSuccess) {
      setToken(tokenResult.data.refreshToken);
    }
  }, [tokenResult]);

  useEffect(() => {
    if (!dialog.open) {
      setToken("");
    }
  }, [dialog.open]);

  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.handleClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        Refresh API User Token
        <IconButton
          color="primary"
          onClick={() => dialog.handleClose()}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container disableGutters>
          <Stack>
            {!token && (
              <Typography variant="body1" mt={2}>
                Refreshing the API User Token will result in the previous token
                being invalidated.
              </Typography>
            )}
            {token && (
              <Stack spacing={2} mt={2}>
                <Typography variant="body1">
                  Copy your API User Token.
                </Typography>
                <CodeSnippet
                  code={token}
                  copyInfo="api_user_token"
                  showViewToggle
                />
              </Stack>
            )}
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dialog.handleClose()}>
          {token ? "Continue" : "Cancel"}
        </Button>
        {!token && (
          <SaveButton color="primary" onClick={clickRefresh}>
            Refresh API User Token
          </SaveButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RefreshToken;
